import { parseAgentSipInvitePayload } from '../../infrastructure/adapters/voiceSocketEventAdapter';
import {
  answerCurrentAgentCall,
  autoAnswerAgentSipInvite,
  getCurrentAgentCall,
  isAgentAnswerBridgeOwning,
  isAgentSessionAlreadyAnswered,
  markAgentCallAnswerPending,
  playRemoteMediaElement,
  waitForAgentClientReady
} from '../../infrastructure/webrtc/voiceAgentWebRtcAdapter';
import getOrCreateVoiceTabSessionId from '../../infrastructure/webrtc/voiceTabSession';
import { VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID } from '../../infrastructure/config/voiceWebRtcConfig';
import { VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS } from '../../domain/voiceEventConstants';
import {
  isVoiceTabAnswerClaimed,
  markVoiceSipAnsweredOnce,
  wasVoiceSipAnsweredOnce
} from '../voiceHangupCoordinator';
import { voiceSocketLog } from '../../../../utils/voiceSocketDebug';
import { isOutgoingCallDirection } from './voiceSocketCallUtils';

export default function agentSipInviteHandler(payload, ports) {
  const parsed = parseAgentSipInvitePayload(payload);
  if (!parsed) {
    voiceSocketLog('agent_sip_invite ignored (invalid payload)', payload);
    return;
  }

  const localTabSessionId = getOrCreateVoiceTabSessionId();
  if (parsed.tabSessionId !== localTabSessionId) {
    voiceSocketLog('agent_sip_invite ignored (tab session mismatch)', {
      callControlId: parsed.callControlId
    });
    return;
  }

  const { outboundDialingCall } = ports.getHandlerState();

  const isInboundLeg = parsed.autoAnswer !== true
    && (
      parsed.direction === 'inbound'
      || !isOutgoingCallDirection(parsed.direction)
    );

  const claimedThisCall = isVoiceTabAnswerClaimed(parsed.callControlId);

  const matchesOutboundDialing = outboundDialingCall?.callControlId === parsed.callControlId;
  const shouldAutoAnswerOutbound = (
    matchesOutboundDialing
    || claimedThisCall
  ) && (
    parsed.autoAnswer === true
    || isOutgoingCallDirection(parsed.direction)
  );

  voiceSocketLog('agent_sip_invite received', {
    callControlId: parsed.callControlId,
    isInboundLeg,
    shouldAutoAnswerOutbound,
    direction: parsed.direction,
    claimedThisCall
  });

  if (isInboundLeg && claimedThisCall) {
    if (wasVoiceSipAnsweredOnce(parsed.callControlId)) {
      voiceSocketLog('agent_sip_invite inbound already answered once', {
        callControlId: parsed.callControlId
      });
      return;
    }
    markVoiceSipAnsweredOnce(parsed.callControlId);
    markAgentCallAnswerPending(parsed.callControlId, { ownedByAnswerBridge: true });
    waitForAgentClientReady({ reason: 'agent_sip_invite_inbound' })
      .then(() => {
        const session = getCurrentAgentCall();
        if (
          session?.direction === 'incoming'
          && !isAgentSessionAlreadyAnswered(session)
        ) {
          return answerCurrentAgentCall({
            incomingCallPromise: Promise.resolve(session)
          });
        }
        return undefined;
      })
      .catch((error) => {
        voiceSocketLog('agent_sip_invite inbound answer failed', {
          message: error instanceof Error ? error.message : String(error)
        });
      });
    voiceSocketLog('agent_sip_invite inbound answer flow', {
      callControlId: parsed.callControlId
    });
    return;
  }

  if (!shouldAutoAnswerOutbound) {
    voiceSocketLog('agent_sip_invite ignored (inbound ring-only; use Answer button)', {
      callControlId: parsed.callControlId
    });
    return;
  }

  if (isAgentAnswerBridgeOwning(parsed.callControlId)) {
    markAgentCallAnswerPending(parsed.callControlId, { ownedByAnswerBridge: true });
    waitForAgentClientReady({ reason: 'agent_sip_invite_bridge' }).catch(() => {});
    voiceSocketLog('agent_sip_invite deferred to answer bridge', {
      callControlId: parsed.callControlId
    });
    return;
  }

  waitForAgentClientReady({ reason: 'agent_sip_invite_outbound' })
    .then(() => autoAnswerAgentSipInvite(parsed.callControlId, {
      timeoutMs: VOICE_AGENT_SIP_AUTO_ANSWER_TIMEOUT_MS
    }))
    .then(() => {
      playRemoteMediaElement(VOICE_WEBRTC_REMOTE_MEDIA_ELEMENT_ID);
      voiceSocketLog('agent_sip_invite auto-answer completed', parsed.callControlId);
    })
    .catch((error) => {
      voiceSocketLog('agent_sip_invite auto-answer failed', {
        callControlId: parsed.callControlId,
        message: error instanceof Error ? error.message : String(error)
      });
    });
}
